import { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
  IconButton,
  Grid,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BusinessIcon from "@mui/icons-material/Business";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Table from "../components/common/table/Table";
import DockerConfigModal from "../components/company-admin/docker-config-modal";
import { getPageByProjectId } from "../Redux/Actions/pageActions";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Loader from "./Loader";

const pages = [
  { id: 1, pageName: "Home Page", lastEdit: "2025-03-27", createdBy: "Alice" },
  { id: 2, pageName: "About Us", lastEdit: "2025-03-26", createdBy: "Bob" },
  { id: 3, pageName: "Contact", lastEdit: "2025-03-25", createdBy: "Carol" },
  { id: 4, pageName: "Products", lastEdit: "2025-03-24", createdBy: "David" },
  { id: 5, pageName: "Services", lastEdit: "2025-03-23", createdBy: "Eve" },
  { id: 6, pageName: "Team", lastEdit: "2025-03-22", createdBy: "Frank" },
  { id: 7, pageName: "Careers", lastEdit: "2025-03-21", createdBy: "Grace" },
  { id: 8, pageName: "Blog", lastEdit: "2025-03-20", createdBy: "Heidi" },
  { id: 9, pageName: "News", lastEdit: "2025-03-19", createdBy: "Ivan" },
  { id: 10, pageName: "Partners", lastEdit: "2025-03-18", createdBy: "Judy" },
  { id: 11, pageName: "FAQ", lastEdit: "2025-03-17", createdBy: "Alice" },
  { id: 12, pageName: "Terms of Service", lastEdit: "2025-03-16", createdBy: "Bob" },
  { id: 13, pageName: "Privacy Policy", lastEdit: "2025-03-15", createdBy: "Carol" },
  { id: 14, pageName: "Support", lastEdit: "2025-03-14", createdBy: "David" },
  { id: 15, pageName: "Dashboard", lastEdit: "2025-03-13", createdBy: "Eve" },
  { id: 16, pageName: "Reports", lastEdit: "2025-03-12", createdBy: "Frank" },
  { id: 17, pageName: "Settings", lastEdit: "2025-03-11", createdBy: "Grace" },
  { id: 18, pageName: "Login", lastEdit: "2025-03-10", createdBy: "Heidi" },
  { id: 19, pageName: "Register", lastEdit: "2025-03-09", createdBy: "Ivan" },
  { id: 20, pageName: "User Profile", lastEdit: "2025-03-08", createdBy: "Judy" },
];

export default function DockerGeneratorPage() {
  const location = useLocation();
  const { projectData } = location.state || {};
  const { pageByProjectData, loading: isLoading } = useSelector(
      (state) => state.pageByProject_id
    );
  // State declarations
  const [dockerName, setDockerName] = useState("");
  const [dockerNameError, setDockerNameError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const pageSize = 10;

  let dispatch = useDispatch();
  

  // Filter pages based on search query (case-insensitive)
  const filteredPages = pageByProjectData?.filter((p) =>
    p?.name?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
    p?._id?.toString().toLowerCase().includes(searchQuery.toLowerCase())
  );


  // Sorting logic for the filtered pages
  const sortedPages = sortField
    ? [...filteredPages].sort((a, b) => {
        const valA = a[sortField];
        const valB = b[sortField];
        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      })
    : filteredPages;

  // const totalItems = pageByProjectData?.length;
  const currentPageData = pageByProjectData?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // setSelectedRows([]);
  };


  const handleSelectRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };


  const handleSelectAll = () => {
    const allIds = pageByProjectData?.map((row) => row._id);
    setSelectedRows(allIds);
  };


  const handleSelectCurrentPage = () => {
      const pageIds = currentPageData?.map((row) => row._id);
      const allSelected = pageIds.every((id) => selectedRows.includes(id));

      if (allSelected) {
        // Deselect only current page rows
        const remaining = selectedRows.filter((id) => !pageIds.includes(id));
        setSelectedRows(remaining);
      } else {
        // Add current page rows to selected list
        const updated = Array.from(new Set([...selectedRows, ...pageIds]));
        setSelectedRows(updated);
      }
  };


  const handleClearAll = () => {
    setSelectedRows([]);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    dispatch(getPageByProjectId(projectData?.id));
  }, [projectData?.id]);

    

  const columns = [
    {
      header: (
        <input
          type="checkbox"
          onChange={handleSelectCurrentPage}
          checked={
            currentPageData?.length > 0 &&
            currentPageData?.every((row) => selectedRows.includes(row._id))
          }
          style={{ width: 16, height: 16 }}
        />
      ),
      accessor: "checkbox",
      cell: (_, row) => (
      <input
        type="checkbox"
        onChange={() => handleSelectRow(row._id)}
        checked={selectedRows.includes(row._id)}
        style={{ width: 16, height: 16 }}
      />
    ),
    },
    {
      header: (
        <Box
          onClick={() => handleSort("name")}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        >
          <Typography variant="body1" fontWeight="bold">
            Page Name
          </Typography>
          {sortField === "name" && (
            <ArrowDownwardIcon
              sx={{
                ml: 0.5,
                transform: sortDirection === "asc" ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.2s",
              }}
            />
          )}
        </Box>
      ),
      accessor: "name",
      cell: (value) => (
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      ),
    },
    {
      header: "Home Page",
      accessor: "is_home",
      cell: (value) => (
        <Typography variant="body2" color="text.secondary">
          {value ? "Yes" : "No"}
        </Typography>
      ),
    },
    {
      header: "Last Edit",
      accessor: "updatedAt",
      cell: (value) => (
        <Typography variant="body2" color="text.secondary">
          {value}
        </Typography>
      ),
    },
  ];

  if(isLoading){
    return <Loader />
  }

  const handleGenerateDocker = () => {
    console.log("Docker Name:", dockerName);
    console.log("Selected Page IDs:", selectedRows);
  };

  const handleDockerNameChange = (e) => {
    const newValue = e.target.value;
    const pattern = /^[a-z0-9._-]+$/;
    if (!pattern.test(newValue)) {
      setDockerNameError(
        "Invalid name. Use only lowercase letters, digits, '-', '_', and '.'"
      );
    } else {
      setDockerNameError("");
    }
    setDockerName(newValue);
  };

  const isGenerateDisabled =
    dockerName?.trim() === "" ||
    selectedRows?.length === 0 ||
    Boolean(dockerNameError);

  const totalItems = filteredPages.length;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      <Paper
        sx={{
          bgcolor: "error.main",
          color: "common.white",
          py: 3,
          boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
        }}
        square
      >
        <Container maxWidth="lg" sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              borderRadius: 2,
              p: 1.5,
              mr: 2,
            }}
          >
            <BusinessIcon fontSize="large" />
          </Box>
          <Typography variant="h5" fontWeight="bold">
            Project Name
          </Typography>
        </Container>
      </Paper>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Docker Details Section */}
        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            mb: 3,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Docker Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Typography sx={{ width: 150, color: "text.secondary" }}>
                  Docker Name:
                </Typography>
                <TextField
                  size="small"
                  variant="outlined"
                  value={dockerName}
                  onChange={handleDockerNameChange}
                  placeholder="Enter Docker Name"
                  error={Boolean(dockerNameError)}
                  helperText={dockerNameError}
                  sx={{ flex: 1, backgroundColor: "white", borderRadius: 1 }}
                />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={{ width: 150, color: "text.secondary" }}>
                  Pages Selected:
                </Typography>
                <Typography>
                  {selectedRows?.length > 0
                    ? `Selected: ${selectedRows?.length}`
                    : "No pages selected"}
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                alignItems: "center",
              }}
            >
              {/* <Button
                variant="contained"
                disabled={isGenerateDisabled}
                onClick={handleGenerateDocker}
                startIcon={<DownloadIcon />}
                sx={{
                  bgcolor: "error.main",
                  color: "common.white",
                  px: 3,
                  py: 1.5,
                  fontWeight: "bold",
                  borderRadius: 1,
                  "&:hover": { bgcolor: "error.dark" },
                }}
              >
                Generate Docker
              </Button> */}
            <DockerConfigModal isGenerateDisabled={isGenerateDisabled} data={projectData} dockerName={dockerName} selectedRows={selectedRows} />
            </Grid>
          </Grid>
        </Paper>


        {/* Pages Table Section */}
        <Paper
          sx={{
            borderRadius: 4,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Pages">
                <DescriptionIcon />
              </Tooltip>
              <Typography variant="h6" fontWeight="bold">
                Pages
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                variant="outlined"
                size="medium"
                onClick={handleSelectAll}
                sx={{ borderRadius: 1 }}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                size="medium"
                onClick={handleClearAll}
                sx={{ borderRadius: 1 }}
              >
                Clear All
              </Button>
              <TextField
                size="small"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: "white", borderRadius: 1 }}
              />
              <Typography variant="body2" color="error">
                {totalItems} Total
              </Typography>
            </Stack>
          </Stack>
          {/* Table Section with Loading & Empty States */}
          {isLoading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : pageByProjectData?.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography>No pages available</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                overflow: "hidden",
              }}
            >
              {pageByProjectData?.length > 0 && (
              <Table
                columns={columns}
                data={filteredPages}
                pagination={{
                  currentPage,
                  pageSize,
                  totalItems: totalItems,
                  onPageChange: handlePageChange,
                }}
                rowHover
                className="bg-white"
              />
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
